export interface PaymentGatewayField {
  key: string;
  label: string;
  type: 'text' | 'email' | 'password' | 'textarea' | 'file';
  required: boolean;
  placeholder: string;
  description?: string;
}

export interface PaymentGatewayConfig {
  gateway_type: string;
  name: string;
  fields: PaymentGatewayField[];
  description?: string;
}

export const paymentGatewayConfigs: PaymentGatewayConfig[] = [
  {
    gateway_type: 'paypal',
    name: 'PayPal',
    description: 'PayPal payment gateway configuration',
    fields: [
      {
        key: 'email',
        label: 'PayPal Email',
        type: 'email',
        required: true,
        placeholder: 'Enter your PayPal email address',
        description:
          'The email address associated with your PayPal business account',
      },
    ],
  },
  {
    gateway_type: 'razorpay',
    name: 'Razorpay',
    description: 'Razorpay payment gateway configuration',
    fields: [
      {
        key: 'public_key',
        label: 'Razorpay Key ID',
        type: 'text',
        required: true,
        placeholder: 'Enter your Razorpay Key ID',
        description: 'Your Razorpay API Key ID from the dashboard',
      },
      {
        key: 'secret_key',
        label: 'Razorpay Key Secret',
        type: 'password',
        required: true,
        placeholder: 'Enter your Razorpay Key Secret',
        description: 'Your Razorpay API Key Secret from the dashboard',
      },
    ],
  },
  {
    gateway_type: 'stripe',
    name: 'Stripe',
    description: 'Stripe payment gateway configuration',
    fields: [
      {
        key: 'public_key',
        label: 'Stripe Publishable Key',
        type: 'text',
        required: true,
        placeholder: 'Enter your Stripe publishable key',
        description: 'Your Stripe publishable key (starts with pk_)',
      },
      {
        key: 'secret_key',
        label: 'Stripe Secret Key',
        type: 'password',
        required: true,
        placeholder: 'Enter your Stripe secret key',
        description: 'Your Stripe secret key (starts with sk_)',
      },
    ],
  },
  {
    gateway_type: 'cashfree',
    name: 'Cashfree',
    description: 'Cashfree payment gateway configuration',
    fields: [
      {
        key: 'public_key',
        label: 'Cashfree App ID',
        type: 'text',
        required: true,
        placeholder: 'Enter your Cashfree App ID',
        description: 'Your Cashfree App ID from the dashboard',
      },
      {
        key: 'secret_key',
        label: 'Cashfree Secret Key',
        type: 'password',
        required: true,
        placeholder: 'Enter your Cashfree secret key',
        description: 'Your Cashfree secret key from the dashboard',
      },
    ],
  },
  {
    gateway_type: 'inrportal',
    name: 'INR Portal',
    description: 'INR Portal payment gateway configuration',
    fields: [
      {
        key: 'secret_key',
        label: 'API Token',
        type: 'password',
        required: true,
        placeholder: 'Enter your API token',
        description: 'Your INR Portal API token',
      },
    ],
  },
  {
    gateway_type: 'coinflexpay',
    name: 'Coin Flex Pay',
    description: 'Coin Flex Pay payment gateway configuration',
    fields: [
      {
        key: 'secret_key',
        label: 'API Token',
        type: 'password',
        required: true,
        placeholder: 'Enter your API token',
        description: 'Your Coin Flex Pay API token',
      },
    ],
  },
  {
    gateway_type: 'binance',
    name: 'Binance',
    description: 'Binance payment gateway configuration',
    fields: [
      {
        key: 'public_key',
        label: 'Binance API Key',
        type: 'text',
        required: true,
        placeholder: 'Enter your Binance API key',
        description: 'Your Binance API key from the account settings',
      },
      {
        key: 'secret_key',
        label: 'Binance Secret Key',
        type: 'password',
        required: true,
        placeholder: 'Enter your Binance secret key',
        description: 'Your Binance secret key from the account settings',
      },
    ],
  },
  // Wallet configuration - no API keys needed
  {
    gateway_type: 'wallet',
    name: 'Wallet',
    description: 'Wallet payment gateway configuration',
    fields: [],
  },
  // Manual payment configuration - no API keys needed
  {
    gateway_type: 'manual',
    name: 'Manual Payment',
    description: 'Manual payment gateway configuration',
    fields: [],
  },
  // Default configuration for unknown gateways
  {
    gateway_type: 'default',
    name: 'Default Gateway',
    description: 'Default payment gateway configuration',
    fields: [
      {
        key: 'public_key',
        label: 'Public Key',
        type: 'text',
        required: false,
        placeholder: 'Enter your public key',
        description: 'Public key for the payment gateway',
      },
      {
        key: 'secret_key',
        label: 'Secret Key',
        type: 'password',
        required: true,
        placeholder: 'Enter your secret key',
        description: 'Secret key for the payment gateway',
      },
    ],
  },
];

// Helper function to get configuration for a specific gateway type
export const getPaymentGatewayConfig = (
  gatewayType: string,
): PaymentGatewayConfig => {
  const config = paymentGatewayConfigs.find(
    config => config.gateway_type === gatewayType,
  );
  return (
    config ||
    paymentGatewayConfigs.find(config => config.gateway_type === 'default')!
  );
};

// Common fields that should be available for all gateways
export const getCommonFields = (): PaymentGatewayField[] => [];

// Helper function to get field configuration for a specific gateway type
export const getPaymentGatewayFields = (
  gatewayType: string,
): PaymentGatewayField[] => {
  const gatewayFields = getPaymentGatewayConfig(gatewayType).fields;
  const commonFields = getCommonFields();

  // Combine common fields with gateway-specific fields
  return [...commonFields, ...gatewayFields];
};
